import api from '../api/apiClient';

const PREFIX = '/alerts/api/v1';

// ---------------------------------------------------------------------------
// In-flight GET dedup (AA-045; mirrors GE-016 in enterpriseAIClient.js)
// ---------------------------------------------------------------------------
// The alerts page fans out one history fetch per concern per load (dashboard
// list, unread total, breakdown sample) alongside the global Header bell, so
// identical concurrent histories shared one promise instead of one request
// each. Keyed on URL + sorted params: unlike GE-016, the params here are
// material (page_size, module, time_range change the rows), so a URL-only key
// would serve one caller's slice to another. The entry lives only for the
// lifetime of the in-flight promise, so a later load still fetches fresh.
const inFlightGets = new Map();

const normParams = (params = {}) => Object.entries(params)
  .filter(([, v]) => v !== undefined && v !== null && v !== '')
  .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
  .map(([k, v]) => `${k}=${String(v)}`)
  .join('&');

function dedupeGet(url, params) {
  const key = `GET ${url}?${normParams(params)}`;
  if (inFlightGets.has(key)) return inFlightGets.get(key);
  const promise = api
    .get(url, { params })
    .then((res) => res.data)
    .finally(() => inFlightGets.delete(key));
  inFlightGets.set(key, promise);
  return promise;
}

export const alertsService = {
  async getAlertById(alertId) {
    if (!alertId) throw new Error('alertId is required');
    const response = await api.get(`${PREFIX}/alerts/${alertId}`);
    return response.data;
  },

  async getAlerts(params = {}) {
    // Get paginated alert history from alert microservice
    return dedupeGet(`${PREFIX}/alerts/history`, params);
  },

  async getDashboardStats(params = {}) {
    // Fetch both stats and history in parallel, then combine into
    // the shape the frontend expects: { kpi_stats, chart_data, alerts, total_records }
    //
    // KPI stats (unread_critical, new_alerts_24h, etc.) must reflect global counts
    // regardless of severity or read-status filters — the stat cards show totals,
    // not filtered subsets (e.g. "Unread Critical" is always the global critical unread count).
    const { severity: _omitSev, unread_only: _omitUnread, ...statsParams } = params;
    const [stats, history] = await Promise.all([
      dedupeGet(`${PREFIX}/alerts/stats`, statsParams),
      dedupeGet(`${PREFIX}/alerts/history`, params),
    ]).then(([s, h]) => [s || {}, h || {}]);

    const bySeverity = stats.by_severity || {};
    const byModule = stats.by_module || {};

    // The service's unread critical count. Without one it is unknown (null), never every critical
    // alert: that fallback counted read alerts as unread (audit A59).
    let unreadCritical = stats.unread_critical == null ? null : Number(stats.unread_critical);
    if (unreadCritical != null && (!Number.isFinite(unreadCritical) || unreadCritical < 0)) {
      unreadCritical = null;
    }

    // Most frequent = highest-count severity only (Critical, High, Medium, etc.)
    let mostFrequentAlert = 'N/A';
    if (Object.keys(bySeverity).length > 0) {
      const entries = Object.entries(bySeverity).sort(([, a], [, b]) => b - a);
      mostFrequentAlert = entries[0][0];
    } else if (Array.isArray(history.alerts) && history.alerts.length > 0) {
      const severityCount = new Map();
      history.alerts.forEach((alert) => {
        const sev = (alert.severity || 'Unknown').toString().trim();
        severityCount.set(sev, (severityCount.get(sev) || 0) + 1);
      });
      let topSeverity = 'N/A';
      let topCount = 0;
      severityCount.forEach((count, sev) => {
        if (count > topCount) {
          topCount = count;
          topSeverity = sev;
        }
      });
      mostFrequentAlert = topSeverity;
    }

    // Alerts per day.
    //
    // The backend aggregates this over the whole window (stats.by_day), which is
    // the only source that can be right: deriving it from `history.alerts` counts
    // the timestamps on ONE PAGE of results, so a 90-day window backed by 200k
    // alerts produced a "trend" describing the most recent 25 of them. The
    // per-page derivation is kept only as a fallback for an older backend.
    let trendLabels = [];
    let trendValues = [];
    if (Array.isArray(stats.by_day) && stats.by_day.length > 0) {
      trendLabels = stats.by_day.map((d) => d.day);
      trendValues = stats.by_day.map((d) => Number(d.count) || 0);
    } else if (Array.isArray(history.alerts) && history.alerts.length > 0) {
      const countsByDay = new Map();
      history.alerts.forEach((alert) => {
        const ts = alert.created_at || alert.timestamp;
        if (!ts) return;
        const day = new Date(ts).toISOString().slice(0, 10); // YYYY-MM-DD
        countsByDay.set(day, (countsByDay.get(day) || 0) + 1);
      });
      const sortedDays = Array.from(countsByDay.keys()).sort();
      trendLabels = sortedDays;
      trendValues = sortedDays.map((d) => countsByDay.get(d));
    }

    return {
      kpi_stats: {
        new_alerts_24h: stats.alerts_last_24h || 0,
        unread_critical: unreadCritical,
        most_frequent_alert: mostFrequentAlert,
        total_alerts: stats.total_alerts || 0,
        // Not measured by the alert service (null): never shown as 0% or 100%.
        delivery_success_rate: stats.delivery_success_rate ?? null,
        alerts_last_7d: stats.alerts_last_7d || 0,
        by_severity: bySeverity,
        by_module: byModule,
        // Severity split per module, window-wide. Consumers that rank surfaces
        // need this; a flat count ranks a noisy low-severity surface above a
        // quiet critical one.
        by_module_severity: stats.by_module_severity || {},
        by_day: Array.isArray(stats.by_day) ? stats.by_day : [],
      },
      // If we have at least 2 distinct days, show a time-series trend.
      // Otherwise fall back to a severity distribution chart so the line
      // doesn't appear as a flat/meaningless segment.
      chart_data: trendLabels.length > 1
        ? {
            labels: trendLabels,
            values: trendValues,
            label: 'Alerts over time',
          }
        : {
            labels: Object.keys(bySeverity),
            values: Object.values(bySeverity),
            label: 'Alerts by Severity',
          },
      alerts: history.alerts || [],
      total_records: history.total || 0,
    };
  },

  async markAlertRead(alertId) {
    // Acknowledge alert (microservice uses "acknowledge" semantics)
    const response = await api.post(`${PREFIX}/alerts/${alertId}/acknowledge`);
    return response.data;
  },

  async markAllAlertsRead(agentId = null) {
    const acknowledgeInBatches = async (alertIds, batchSize = 10) => {
      let succeeded = 0;
      for (let i = 0; i < alertIds.length; i += batchSize) {
        const batch = alertIds.slice(i, i + batchSize);
        const results = await Promise.allSettled(
          batch.map((id) => api.post(`${PREFIX}/alerts/${id}/acknowledge`))
        );
        succeeded += results.filter((r) => r.status === 'fulfilled').length;
      }
      return succeeded;
    };

    try {
      // Preferred path: backend bulk acknowledge
      const response = await api.post(`${PREFIX}/alerts/acknowledge-all`, {}, {
        params: {
          ...(agentId ? { agent_id: agentId } : {}),
        },
      });
      return response.data || { success: true };
    } catch (bulkErr) {
      const status = bulkErr?.response?.status;
      // If backend rejects this route/method, use safe low-concurrency fallback.
      // This avoids flooding the browser/network with hundreds of simultaneous requests.
      // Backward-compatible fallback for older backend deployments
      const historyRes = await api.get(`${PREFIX}/alerts/history`, {
        params: {
          page: 1,
          page_size: 2000,
          ...(agentId ? { agent_id: agentId } : {}),
          unread_only: true,
        },
      });
      const alerts = historyRes.data?.alerts || [];
      const idField = (a) => a.id ?? a._id ?? a.alert_id;
      const ids = alerts.map(idField).filter(Boolean);
      const updated = await acknowledgeInBatches(ids, 10);
      return {
        success: true,
        fallback: true,
        bulk_error_status: status,
        attempted: ids.length,
        updated,
      };
    }
  },
};
